import { useEffect, useMemo, useState, useCallback } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { toast } from "sonner";
import {
  ArrowLeft,
  CalendarPlus,
  ChevronDown,
  Download,
  FileText,
  Globe2,
  GraduationCap,
  Layers,
  Loader2,
  Share2,
  Sparkles,
  Upload,
  Users,
  X,
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
// Phase 115 — UserMenu + StartBuildingButton removed; AppShell owns global nav.
import ExportButtons from "../components/ExportButtons";
import { DeckPreview, WorksheetPreview } from "../components/ArtifactPreview";
import { DeckMirroringAudit } from "../components/DeckMirroringAudit";
import SeriesBreadcrumb from "../components/SeriesBreadcrumb";
import ShareLessonDialog from "../components/ShareLessonDialog";
import PostGenScheduleDialog from "../components/PostGenScheduleDialog";
import UnitDocument from "../components/UnitDocument";
import { parseReportingSections } from "../lib/reportingSections";
import { markdownComponents } from "../components/LessonDocument";
import { api, getActivity, shareActivity, unshareActivity, enablePublicShare, listPlannerEventsByActivity, deletePlannerEvent } from "../lib/api";
import { pushRecentLesson } from "../lib/recentLessons";
import MarkTaughtButton from "../components/MarkTaughtButton";
import AddToGradebookButton from "../components/AddToGradebookButton";
import { useAuth } from "../contexts/AuthContext";
import { splitLessonSections } from "../lib/lessonWorkspaceSections";
import { track } from "../lib/analytics";
import UnitLinkedSupportPlans from "../components/classroom-support/UnitLinkedSupportPlans";

/**
 * Lesson Workspace (Phase 107) — replaces the modal "View" dialog.
 *
 * Route: /lesson/:id
 *
 * Mobile-first, full-page surface with:
 *   • header (title, description, deduped tag pills)
 *   • persistent export bar (sticky on mobile, fixed on desktop)
 *   • four tabs — Overview / Teacher Script / Student Materials / Reporting
 *
 * Handles all three activity output types — lessons, units, summative
 * tasks. Falls back to a friendly "loading / not found" state when
 * the URL points at a stale id.
 */
const TABS = [
  { id: "overview", label: "Overview" },
  { id: "script", label: "Teacher Script" },
  { id: "students", label: "Student Materials" },
  { id: "preview", label: "Preview" },
  { id: "reporting", label: "Reporting" },
];

// Format an ISO date (YYYY-MM-DD) as a short local label, e.g. "Mon, Sep 14".
const fmtPlacementDate = (iso) => {
  if (!iso || !/^\d{4}-\d{2}-\d{2}$/.test(iso)) return iso || "";
  const [y, m, d] = iso.split("-").map(Number);
  return new Date(y, m - 1, d).toLocaleDateString(undefined, {
    weekday: "short",
    month: "short",
    day: "numeric",
  });
};

const LessonWorkspace = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [activity, setActivity] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tab, setTab] = useState("overview");
  const [shareOpen, setShareOpen] = useState(false);
  const [plannerOpen, setPlannerOpen] = useState(false);
  const [plannerPlacements, setPlannerPlacements] = useState([]);
  const [placementBusy, setPlacementBusy] = useState(null);

  const loadPlacements = useCallback(() => {
    if (!activity?.id) return;
    const owner = !!user && activity.teacher_id === user.user_id;
    const paid = Boolean(
      user?.subscription?.is_active || user?.has_math_engine_access,
    );
    const schedulable =
      activity.output_type === "lesson" ||
      activity.output_type === "unit" ||
      activity.output_type === "summative_task";
    if (!owner || !paid || !schedulable) {
      setPlannerPlacements([]);
      return;
    }
    listPlannerEventsByActivity(activity.id)
      .then((rows) => setPlannerPlacements(Array.isArray(rows) ? rows : []))
      .catch(() => setPlannerPlacements([]));
  }, [activity?.id, activity?.teacher_id, activity?.output_type, user]);

  useEffect(() => {
    loadPlacements();
  }, [loadPlacements]);

  const handleUnschedule = async (ev) => {
    setPlacementBusy(ev.id);
    try {
      await deletePlannerEvent(ev.id);
      setPlannerPlacements((arr) => arr.filter((x) => x.id !== ev.id));
      toast.success("Removed from Planner");
    } catch (e) {
      toast.error(
        e?.response?.data?.detail || "Could not remove from Planner.",
      );
    } finally {
      setPlacementBusy(null);
    }
  };

  useEffect(() => {
    let abort = false;
    setLoading(true);
    setError(null);
    getActivity(id)
      .then((data) => {
        if (!abort) {
          setActivity(data);
          // Phase 115.7 — pin the opened lesson to the recent-lessons
          // pill row in the in-page top bar.
          pushRecentLesson(
            data?.id,
            data?.activity_name || "",
          );
        }
      })
      .catch((e) => {
        if (!abort) {
          setError(
            e?.response?.status === 404
              ? "We couldn't find this lesson — it may have been deleted."
              : e?.response?.data?.detail || "Could not load this lesson.",
          );
        }
      })
      .finally(() => {
        if (!abort) setLoading(false);
      });
    return () => {
      abort = true;
    };
  }, [id]);

  // Re-fetch the activity after a `taught` toggle so the LessonWorkspace
  // header pill flips in real time without a hard reload.
  const refreshActivity = () => {
    getActivity(id).then((data) => setActivity(data)).catch(() => {});
  };

  const effectiveMarkdown =
    activity?.lesson_markdown ||
    activity?.unit_markdown ||
    activity?.summative_markdown ||
    "";

  const sections = useMemo(
    () => splitLessonSections(effectiveMarkdown),
    [effectiveMarkdown],
  );

  // Build the deduplicated tag pill list. The legacy modal sometimes
  // showed the same transferable skill twice (once as inputs.skill_focus
  // and again as activity.transferable_skills[0]); dedupe by lowercase
  // string so we only emit one pill per concept.
  const tagPills = useMemo(() => {
    const seen = new Set();
    const out = [];
    const push = (label, kind) => {
      if (!label) return;
      const key = String(label).trim().toLowerCase();
      if (!key || seen.has(key)) return;
      seen.add(key);
      out.push({ label, kind });
    };
    const inputs = activity?.inputs || {};
    push(inputs.grade, "meta");
    push(inputs.subject, "meta");
    push(inputs.time, "meta");
    push(
      activity?.output_type === "unit"
        ? "Unit plan"
        : activity?.output_type === "summative_task"
        ? "Summative task"
        : activity?.output_type === "supply_plan"
        ? "Supply plan"
        : inputs.activity_type,
      "meta",
    );
    // Skill pills: render the canonical transferable_skills array (one pill per
    // skill). Fall back to the joined skill_focus string ONLY when the array is
    // absent (legacy single-skill docs). Never both — pushing the joined string
    // alongside the array produced a duplicate combined pill on multi-skill
    // units (the dedupe below only caught the legacy skill_focus==skills[0]
    // case). Jun 2026 defect.
    const skills = activity?.transferable_skills || [];
    if (skills.length) {
      skills.forEach((t) => push(t, "skill"));
    } else {
      push(inputs.skill_focus, "skill");
    }
    return out.slice(0, 10);
  }, [activity]);

  // --- loading / not-found / unauthed states -----------------------
  if (loading) {
    return (
      <Frame>
        <div
          data-testid="lesson-workspace-loading"
          className="max-w-3xl mx-auto px-6 py-24 text-center"
        >
          <Loader2 className="h-6 w-6 animate-spin text-[#4a6984] mx-auto mb-3" />
          <p className="text-[#4a6984]/80 font-medium">Loading your lesson…</p>
        </div>
      </Frame>
    );
  }
  if (error || !activity) {
    return (
      <Frame>
        <div className="max-w-2xl mx-auto px-6 py-16 text-center">
          <h1 className="font-black text-3xl text-[#4a6984] tracking-tight mb-3">
            Lesson not found
          </h1>
          <p className="text-[#4a6984]/80 mb-8">
            {error || "This lesson may have been deleted or moved."}
          </p>
          <Link
            to="/dashboard?tab=library"
            data-testid="lesson-workspace-back-to-library"
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-[#96ded1] text-[#1c2d3d] font-extrabold text-sm hover:bg-[#96ded1]/80"
          >
            <ArrowLeft className="h-4 w-4" /> Back to library
          </Link>
        </div>
      </Frame>
    );
  }

  const isSupply = activity.output_type === "supply_plan";
  const isSummative = activity.output_type === "summative_task";
  const hasScript = !!activity.teacher_script_markdown;
  const isOwner = !!user && activity.teacher_id === user.user_id;
  const sharedToCommunity = !!activity.shared_to_community;

  const handleCommunityToggle = async () => {
    try {
      if (sharedToCommunity) {
        await unshareActivity(activity.id);
        setActivity({ ...activity, shared_to_community: false });
        toast.success("Removed from the community feed.");
      } else {
        await shareActivity(activity.id);
        setActivity({ ...activity, shared_to_community: true });
        toast.success("Shared with the Atomic Edu community.");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not update share state.");
    }
  };

  // Phase 107 compound — Google Classroom shareable permalink. The
  // existing /api/p/{token} URL is already crawler-friendly + cookie-
  // free, so a Classroom post pasting the link renders a clean preview
  // card. This action enables (or fetches) the public link and copies
  // it directly to clipboard so the teacher can paste into a Classroom
  // assignment without opening the full Share dialog.
  const handleClassroomLink = async () => {
    try {
      const res = await enablePublicShare(activity.id);
      const url = res?.share_url;
      if (!url) {
        toast.error("Could not generate the link.");
        return;
      }
      // Try the modern clipboard API; gracefully fall back when running
      // inside iOS Safari without secure-context permission.
      try {
        await navigator.clipboard.writeText(url);
        toast.success("Classroom-ready link copied", {
          description: "Paste it into a Google Classroom post or assignment.",
        });
      } catch {
        toast.success("Public link is live", {
          description: url,
          duration: 8000,
        });
      }
      try {
        track("classroom_link_copied", { activity_id: activity.id });
      } catch {
        /* analytics is best-effort */
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not generate the link.");
    }
  };

  return (
    <Frame>
      {/* Title + tags */}
      <header className="max-w-7xl mx-auto px-4 sm:px-6 pt-6 sm:pt-10">
        <Link
          to="/dashboard?tab=library"
          data-testid="lesson-workspace-back"
          className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-[0.18em] text-[#4a6984]/70 hover:text-[#4a6984] mb-4"
        >
          <ArrowLeft className="h-3.5 w-3.5" /> Back to library
        </Link>
        <p className="text-[10px] font-extrabold uppercase tracking-[0.22em] text-[#4a6984]/70">
          {isSupply
            ? "Emergency Supply Plan"
            : isSummative
            ? "Summative Task"
            : activity.output_type === "unit"
            ? "Unit Plan"
            : "Lesson"}
        </p>
        {/* Phase 119.E series-builder — breadcrumb of lessons in the
            same series (shared series_root_id). Hidden when this
            activity isn't part of any series. */}
        {(activity.output_type === "lesson" || activity.output_type === "unit") ? (
          <SeriesBreadcrumb activityId={activity.id} />
        ) : null}
        <h1
          data-testid="lesson-workspace-title"
          className="mt-2 font-black text-3xl sm:text-4xl lg:text-5xl text-[#4a6984] tracking-tight leading-[1.1]"
        >
          {activity.activity_name || "Untitled lesson"}
        </h1>
        {activity.tagline && (
          <p
            data-testid="lesson-workspace-tagline"
            className="mt-3 text-[#4a6984]/85 font-medium text-base sm:text-lg max-w-3xl"
          >
            {activity.tagline}
          </p>
        )}
        {/* Phase 126.L — Linked Classroom Support plans for this unit. */}
        <UnitLinkedSupportPlans
          unitId={activity.id}
          isUnit={activity.output_type === "unit"}
        />
        {/* Phase 114.D.2 — Task component badge. Appears when this
            lesson activity has been tagged as a component of a
            distributed unit task. Click → navigate to the parent unit
            so the teacher can review the task as a whole. */}
        {activity.task_component_name && activity.task_component_unit_id ? (
          <div
            className="mt-4 inline-flex items-center gap-2 rounded-md border border-[#96ded1] bg-[#96ded1]/20 px-3 py-1.5 text-[11px] font-extrabold text-[#1c2d3d]"
            data-testid="lesson-workspace-task-component-badge"
          >
            <span className="inline-flex items-center justify-center h-4 w-4 rounded-sm bg-[#1c2d3d] text-white">
              <Layers className="h-2.5 w-2.5" />
            </span>
            <span className="tracking-tight">
              Task component:{" "}
              <span className="text-[#4a6984] font-bold">
                {activity.task_component_name}
              </span>
            </span>
            <button
              type="button"
              onClick={() =>
                window.open(
                  `/lesson/${activity.task_component_unit_id}`,
                  "_self",
                )
              }
              className="text-[10.5px] font-bold underline-offset-2 hover:underline text-[#4a6984] hover:text-[#1c2d3d]"
              data-testid="lesson-workspace-task-component-open-unit"
            >
              Open unit task →
            </button>
          </div>
        ) : null}
        {/* Phase 138.F — "Build a follow-up from the same source" CTA.
            Surfaces on lessons that were built via Build-from-resource
            so teachers can quickly chain a Day 2 / recap activity / a
            quiz from the same source material. We never stored the
            source itself (privacy promise), so we deep-link to the
            Build-from-resource flow with grade/subject/skill pre-filled
            and ask the teacher to re-upload. */}
        {activity.built_from_resource ? (
          <div
            className="mt-4 rounded-md border-2 border-[#96ded1] bg-[#96ded1]/15 px-4 py-3 flex flex-col sm:flex-row sm:items-center gap-3 justify-between"
            data-testid="lesson-workspace-built-from-resource"
          >
            <div className="min-w-0">
              <p className="text-[10px] font-extrabold uppercase tracking-[0.22em] text-[#1c2d3d]">
                Built from a resource
              </p>
              <p className="text-xs text-[#4a6984]/85 font-medium mt-0.5">
                Chain a follow-up from the same source — Day 2 / a recap /
                a quick quiz. We&apos;ll prefill the grade and skill;
                you&apos;ll just re-upload your file.
              </p>
            </div>
            <button
              type="button"
              onClick={() => {
                // Canonicalise the stored skill_focus before encoding —
                // older activities persisted "Critical Thinking and
                // Problem Solving" (with 'and') while the canonical
                // option enum uses '&'. Without this normalisation the
                // controlled Select on the generator would fall back
                // to its placeholder because the value doesn't appear
                // in its option list.
                const canonSkill = (activity.inputs?.skill_focus || "").replace(
                  /\band\b/g,
                  "&",
                );
                const params = new URLSearchParams({
                  mode: "resource",
                  followup_from: activity.id,
                  grade: activity.inputs?.grade || "",
                  subject: activity.inputs?.subject || "",
                  skill_focus: canonSkill,
                });
                track("lesson_workspace_followup_from_resource_clicked", {
                  activity_id: activity.id,
                });
                navigate(`/generator?${params.toString()}`);
              }}
              data-testid="lesson-workspace-followup-from-resource"
              className="ae-cta hover:ae-cta h-10 px-4 rounded-md font-extrabold inline-flex items-center justify-center gap-2 shrink-0 text-sm"
            >
              <Upload className="h-3.5 w-3.5" />
              Build a follow-up
            </button>
          </div>
        ) : null}
        {tagPills.length > 0 && (
          <div
            className="mt-5 flex flex-wrap gap-2"
            data-testid="lesson-workspace-tags"
          >
            {tagPills.map((t, i) => (
              <span
                key={`${t.label}-${i}`}
                className={
                  t.kind === "skill"
                    ? "ae-pill !bg-[#96ded1]/25 !border-[#96ded1]/60 !text-[#1c2d3d]"
                    : "ae-pill"
                }
              >
                {t.label}
              </span>
            ))}
          </div>
        )}
      </header>

      {/* Persistent export bar — sticky just under the page header so
          it stays in reach on mobile. On desktop it becomes a horizontal
          ribbon, on phones it wraps and uses smaller buttons. */}
      <div
        data-testid="lesson-workspace-export-bar"
        className="sticky top-[60px] z-30 bg-[#f4f5f7]/90 backdrop-blur-sm border-y border-[#4a6984]/10 mt-6"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 flex flex-wrap items-center gap-2">
          <MarkTaughtButton
            activity={activity}
            onChange={refreshActivity}
            size="md"
            data-testid="lesson-workspace-mark-taught"
          />
          <AddToGradebookButton
            activity={activity}
            isOwner={isOwner}
            isPaid={Boolean(
              user?.subscription?.is_active || user?.has_math_engine_access
            )}
          />
          {isOwner &&
            Boolean(
              user?.subscription?.is_active || user?.has_math_engine_access
            ) &&
            (activity.output_type === "lesson" ||
              activity.output_type === "unit" ||
              isSummative) && (
              <button
                type="button"
                onClick={() => setPlannerOpen(true)}
                data-testid="lesson-workspace-add-to-planner"
                title="Add this to your Planner"
                className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-[#4a6984]/20 bg-white text-[#1c2d3d] text-xs font-extrabold hover:bg-[#96ded1]/20 hover:border-[#96ded1]"
              >
                <CalendarPlus className="h-3.5 w-3.5" /> Add to Planner
              </button>
            )}
          {plannerPlacements.map((ev) => (
            <span
              key={ev.id}
              data-testid={`lesson-planner-placement-${ev.id}`}
              className="inline-flex items-center gap-1.5 pl-3 pr-1.5 py-2 rounded-lg border border-[#96ded1] bg-[#96ded1]/25 text-[#1c2d3d] text-xs font-extrabold"
            >
              <CalendarPlus className="h-3.5 w-3.5" />
              Scheduled {fmtPlacementDate(ev.date)}
              {ev.period_label ? ` \u00b7 ${ev.period_label}` : ""}
              <button
                type="button"
                onClick={() => handleUnschedule(ev)}
                disabled={placementBusy === ev.id}
                title="Remove from Planner"
                data-testid={`lesson-unschedule-${ev.id}`}
                className="ml-0.5 rounded p-0.5 hover:bg-[#1c2d3d]/10 disabled:opacity-50"
              >
                {placementBusy === ev.id ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                ) : (
                  <X className="h-3.5 w-3.5" />
                )}
              </button>
            </span>
          ))}
          <span className="hidden sm:block w-px h-5 bg-[#4a6984]/15 mx-1" aria-hidden="true" />
          <ExportButtons
            activityId={activity.id}
            activityName={activity.activity_name}
            isUnit={activity.output_type === "unit"}
            isSummative={isSummative}
            hasScript={hasScript}
            hasHandout={
              !activity.output_type?.startsWith("math_") &&
              /Student Handout|Exit Ticket|Worksheet/i.test(effectiveMarkdown)
            }
            hasMathHandout={
              activity.output_type?.startsWith("math_") &&
              activity.output_type !== "math_activation" &&
              activity.output_type !== "math_unit_summary" &&
              /Student Worksheet|Student Diagnostic|Exit Ticket|Student-Facing/i.test(
                effectiveMarkdown,
              )
            }
            hasSkillEvidence={
              Array.isArray(activity.student_sheets) &&
              activity.student_sheets.some(
                (s) => s && (s.sheet_type === "skill_exit_ticket" || s._skillsfacilitator_exit_ticket)
              )
            }
            hasReading={!!activity.reading_markdown}
            readingTextType={activity.reading_text_type || null}
            readingModes={activity.reading_modes || []}
            showPackage={
              !isSupply && !isSummative &&
              activity.output_type !== "math_activation" &&
              activity.output_type !== "math_unit_summary"
            }
            size="sm"
            onlyKeys={
              isSummative
                ? ["pdf", "docx"]
                : isSupply
                ? ["pdf", "slides"]
                : activity.output_type === "math_activation"
                ? ["pdf", "docx", "slides"]
                : activity.output_type === "math_diagnostic" ||
                  activity.output_type === "math_exit_review"
                ? ["pdf", "worksheet", "docx"]
                : activity.output_type === "math_unit_summary"
                ? ["pdf", "docx"]
                : activity.output_type === "math_lesson1" ||
                  activity.output_type === "math_lesson2"
                ? ["pdf", "worksheet", "docx", "slides"]
                : undefined
            }
          />
          {isOwner && (activity.output_type === "lesson" || activity.output_type === "unit") && (
            <Link
              to={`/generator?ground_lesson_id=${encodeURIComponent(activity.id)}`}
              data-testid="lesson-workspace-write-next"
              title="Write the next lesson grounded in this one"
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-[#96ded1] bg-[#96ded1]/20 text-[#1c2d3d] text-xs font-extrabold hover:bg-[#96ded1]/35 hover:border-[#1c9e7e]"
            >
              <Sparkles className="h-3.5 w-3.5" />
              Write next →
            </Link>
          )}
          {isOwner && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  type="button"
                  data-testid="lesson-workspace-share-menu"
                  className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-white border border-[#4a6984]/15 text-[#1c2d3d] text-xs font-extrabold hover:bg-[#f4f5f7]"
                >
                  <Share2 className="h-3.5 w-3.5" /> Share
                  <ChevronDown className="h-3.5 w-3.5 -mr-0.5" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                align="end"
                className="bg-white border-[#4a6984]/15 w-[320px]"
                data-testid="lesson-workspace-share-menu-content"
              >
                <DropdownMenuLabel className="text-[10px] uppercase tracking-[0.18em] text-[#4a6984]/70">
                  Share this lesson
                </DropdownMenuLabel>
                <DropdownMenuItem
                  onSelect={(e) => {
                    e.preventDefault();
                    handleCommunityToggle();
                  }}
                  data-testid="lesson-workspace-share-community"
                  className="cursor-pointer flex-col items-start gap-1 py-3"
                >
                  <span className="inline-flex items-center gap-2 font-extrabold text-sm text-[#1c2d3d]">
                    <Users className="h-3.5 w-3.5" />
                    {sharedToCommunity
                      ? "Remove from Atomic Edu community"
                      : "Share with the Atomic Edu community"}
                  </span>
                  <span className="text-[11px] text-[#4a6984]/75 leading-snug">
                    Teachers on a paid plan can preview and import a copy.
                    Your name, notes, and class details stay private.
                  </span>
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onSelect={(e) => {
                    e.preventDefault();
                    handleClassroomLink();
                  }}
                  data-testid="lesson-workspace-share-classroom"
                  className="cursor-pointer flex-col items-start gap-1 py-3"
                >
                  <span className="inline-flex items-center gap-2 font-extrabold text-sm text-[#1c2d3d]">
                    <GraduationCap className="h-3.5 w-3.5" />
                    Copy Google Classroom link
                  </span>
                  <span className="text-[11px] text-[#4a6984]/75 leading-snug">
                    One-click copy — paste into a Google Classroom post or
                    assignment. Renders a clean preview card; no sign-in
                    required for students or guests.
                  </span>
                </DropdownMenuItem>
                <DropdownMenuItem
                  onSelect={(e) => {
                    e.preventDefault();
                    setShareOpen(true);
                  }}
                  data-testid="lesson-workspace-share-public"
                  className="cursor-pointer flex-col items-start gap-1 py-3"
                >
                  <span className="inline-flex items-center gap-2 font-extrabold text-sm text-[#1c2d3d]">
                    <Globe2 className="h-3.5 w-3.5" />
                    Public link &amp; social-media preview
                  </span>
                  <span className="text-[11px] text-[#4a6984]/75 leading-snug">
                    Same link, plus the OG preview card teachers see on
                    LinkedIn, X, or Facebook. Opens the full dialog with
                    revoke + view-count controls.
                  </span>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
      </div>

      {/* Tabs — horizontally scrollable on mobile, no horizontal page
          scroll. */}
      <nav
        data-testid="lesson-workspace-tabs"
        className="max-w-7xl mx-auto px-4 sm:px-6 mt-6 border-b border-[#4a6984]/10 overflow-x-auto"
      >
        <div className="flex items-center gap-1 min-w-max">
        {TABS.map((t) => {
          const hasStudentMaterials =
            (activity.student_sheets?.length || 0) > 0 ||
            sections.students.length > 0 ||
            !!activity.reading_markdown;
          // Phase 127 — Preview tab is enabled when at least one of the
          // student-facing artifacts (deck OR student-sheets) is
          // exportable. `student_sheets` is the canonical source for
          // the worksheet preview (same artifact bundled into the
          // Complete Lesson Package — handout + exit ticket).
          const hasStudentSheets = (activity.student_sheets?.length || 0) > 0;
          const hasDeckSource =
            activity.output_type !== "unit" &&
            activity.output_type !== "math_unit_summary";
          const hasPreviewable = hasStudentSheets || hasDeckSource;
          const disabled =
            (t.id === "script" && !hasScript) ||
            (t.id === "students" && (!hasStudentMaterials || activity.output_type === "unit")) ||
            (t.id === "preview" && !hasPreviewable);
            return (
              <button
                key={t.id}
                type="button"
                disabled={disabled}
                onClick={() => setTab(t.id)}
                data-testid={`lesson-workspace-tab-${t.id}`}
                className={
                  "px-4 py-3 -mb-px font-extrabold uppercase tracking-[0.12em] text-xs transition-colors whitespace-nowrap " +
                  (disabled
                    ? "text-[#4a6984]/30 cursor-not-allowed"
                    : tab === t.id
                    ? "text-[#1c2d3d] border-b-2 border-[#96ded1]"
                    : "text-[#4a6984]/65 hover:text-[#4a6984]")
                }
              >
                {t.id === "overview" && isSummative ? "Task Overview" : t.label}
              </button>
            );
          })}
        </div>
      </nav>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-8 pb-24 min-w-0 [&_.ae-prose]:break-words [&_.ae-prose_a]:break-all sm:[&_.ae-prose_a]:break-words">
        {tab === "overview" && (
          activity.output_type === "unit" ? (
            <UnitDocument
              activity={activity}
              onActivityUpdate={refreshActivity}
              enableRefine={false}
              bare
            />
          ) : (
            <OverviewTab
              sections={sections}
              hasScript={hasScript}
              lessonId={activity.id}
              hasStudentMaterials={
                (activity.student_sheets?.length || 0) > 0 ||
                sections.students.length > 0 ||
                !!activity.reading_markdown
              }
            />
          )
        )}
        {tab === "script" && (
          <ScriptTab script={activity.teacher_script_markdown} />
        )}
        {tab === "students" && (
          <StudentsTab activity={activity} sections={sections} />
        )}
        {tab === "preview" && (
          <PreviewTab activity={activity} effectiveMarkdown={effectiveMarkdown} />
        )}
        {tab === "reporting" && (
          <ReportingTab reportingMarkdown={sections.reporting} />
        )}
      </main>

      {/* Public-link share dialog (reuses the existing modal). */}
      {shareOpen && (
        <ShareLessonDialog
          open={shareOpen}
          onOpenChange={setShareOpen}
          activityId={activity.id}
          activityName={activity.activity_name}
        />
      )}

      {/* Add-to-Planner scheduler — reuses the post-generation dialog.
          It auto-opens on mount and gates itself to Pro + onboarded users. */}
      {plannerOpen && (
        <PostGenScheduleDialog
          key={`plan-${activity.id}`}
          activity={activity}
          onDismiss={() => {
            setPlannerOpen(false);
            loadPlacements();
          }}
        />
      )}
    </Frame>
  );
};

const Frame = ({ children }) => (
  <div className="bg-[#f4f5f7] overflow-x-hidden">
    {children}
  </div>
);

/* ------------------------------------------------------------------
 * Tab components
 * ----------------------------------------------------------------*/

const OverviewTab = ({ sections, hasScript, hasStudentMaterials, lessonId }) => (
  <div data-testid="lesson-workspace-overview" className="ae-prose">
    {sections.overview ? (
      <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>
        {sections.overview}
      </ReactMarkdown>
    ) : (
      <p className="text-[#4a6984]/70 italic">
        This lesson has no overview content.
      </p>
    )}
    {(hasScript || hasStudentMaterials) && (
      <div
        data-testid="lesson-workspace-overview-pointers"
        className="mt-8 pt-6 border-t border-[#4a6984]/10 text-sm text-[#4a6984]/75 space-y-1"
      >
        {hasScript && (
          <p>
            <strong className="text-[#1c2d3d]">→ Teacher Script tab:</strong>{" "}
            the full beat-by-beat script for running this lesson.
          </p>
        )}
        {hasStudentMaterials && (
          <p>
            <strong className="text-[#1c2d3d]">→ Student Materials tab:</strong>{" "}
            every worksheet, handout, exit ticket, and reading.
          </p>
        )}
      </div>
    )}
    {lessonId && (
      <div
        data-testid="lesson-workspace-lesson-id"
        className="mt-10 pt-4 border-t border-[#4a6984]/10 text-[11px] text-[#4a6984]/55"
      >
        <span className="font-bold uppercase tracking-[0.16em] mr-2">Lesson ID</span>
        <button
          type="button"
          onClick={() => {
            try {
              navigator.clipboard?.writeText(lessonId);
              toast.success("Lesson ID copied");
            } catch {
              /* clipboard unavailable — id is still selectable below */
            }
          }}
          data-testid="lesson-workspace-lesson-id-copy"
          title="Click to copy"
          className="font-mono select-all break-all text-[#4a6984] hover:text-[#1c2d3d] underline decoration-dotted underline-offset-2 transition-colors"
        >
          {lessonId}
        </button>
      </div>
    )}
  </div>
);

const ScriptTab = ({ script }) => {
  if (!script) {
    return (
      <p className="text-[#4a6984]/70 italic" data-testid="lesson-workspace-no-script">
        This lesson doesn&apos;t have a teacher script yet.
      </p>
    );
  }
  return (
    <div
      data-testid="lesson-workspace-script"
      className="ae-prose ae-prose-script max-w-3xl mx-auto"
    >
      {/* Larger type + tighter max width — designed to read on a phone
          propped on the desk mid-lesson. */}
      <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>
        {script}
      </ReactMarkdown>
    </div>
  );
};

const StudentsTab = ({ activity, sections }) => {
  // Phase 107.E1 — `student_sheets` is the canonical, structured source
  // for every student-facing material. If it exists we render those
  // sheets directly (they were authored as student-eyes-only per the
  // Phase 31 prompt) and DO NOT fall back to the lesson_markdown
  // worksheet/exit-ticket sections, which sometimes accidentally
  // copied a teacher guiding question instead of the actual student
  // task. The lesson_markdown sections only render when student_sheets
  // is missing/empty (older activities pre-Phase 101).
  const sheets = Array.isArray(activity?.student_sheets)
    ? activity.student_sheets.filter((s) => s && typeof s === "object")
    : [];
  const hasSheets = sheets.length > 0;
  const hasInlineFallback = !hasSheets && sections.students.length > 0;
  const hasReading = !!activity.reading_markdown;
  if (!hasSheets && !hasInlineFallback && !hasReading) {
    return (
      <p
        className="text-[#4a6984]/70 italic"
        data-testid="lesson-workspace-no-students"
      >
        This lesson doesn&apos;t include any student-facing materials.
      </p>
    );
  }
  return (
    <div className="space-y-10" data-testid="lesson-workspace-students">
      {/* Canonical student sheets — student_sheets[] is the source of
          truth per Phase 31. */}
      {sheets.map((sheet, i) => {
        const orient = (sheet.orientation || "portrait").toLowerCase();
        return (
          <section
            key={`sheet-${i}`}
            data-testid={`lesson-workspace-students-sheet-${i}`}
            className="rounded-xl border border-[#4a6984]/15 bg-white p-5 sm:p-7"
          >
            <div className="flex flex-wrap items-start justify-between gap-3 mb-4 pb-4 border-b border-[#4a6984]/10">
              <div>
                <p className="text-[10px] font-extrabold uppercase tracking-[0.18em] text-[#4a6984]/65">
                  {String(sheet.sheet_type || "worksheet").replace(/_/g, " ")}
                  {orient === "landscape" ? " · Landscape" : ""}
                </p>
                <h3 className="font-extrabold text-lg text-[#1c2d3d] tracking-tight mt-0.5">
                  {sheet.title || `Student Sheet ${i + 1}`}
                </h3>
              </div>
              {/* Per-sheet download button removed — the broken
                  `onlyKeys=["handout"]` filter never matched the
                  internal worksheet pieceKey so it never rendered.
                  The "Download every student sheet" button below the
                  list bundles everything into one PDF, which is the
                  actual workflow teachers use. */}
            </div>
            {sheet.include_name_date && (
              <p className="text-[11px] uppercase tracking-[0.16em] text-[#4a6984]/55 mb-3">
                Name: ______________________ &nbsp;·&nbsp; Date: ____________
              </p>
            )}
            <div className="ae-prose">
              <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>
                {sheet.content_markdown || ""}
              </ReactMarkdown>
            </div>
          </section>
        );
      })}
      {/* Legacy fallback — only when there's no student_sheets payload
          (pre-Phase 101 activities). */}
      {hasInlineFallback &&
        sections.students.map((sec, i) => (
          <section
            key={`legacy-${i}`}
            data-testid={`lesson-workspace-students-legacy-${i}`}
            className="ae-prose rounded-xl border border-amber-200/60 bg-amber-50/30 p-5"
          >
            <p className="text-[10px] uppercase tracking-[0.18em] text-amber-700/80 font-extrabold mb-2">
              Legacy inline section · regenerate to migrate
            </p>
            <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>
              {sec}
            </ReactMarkdown>
          </section>
        ))}
      {hasReading && (
        <section
          data-testid="lesson-workspace-students-reading"
          className="ae-prose pt-8 border-t-2 border-dashed border-[#96ded1]/50"
        >
          <p className="font-extrabold uppercase tracking-[0.18em] text-[10px] text-[#4a6984]/70 mb-2 flex items-center gap-2 flex-wrap">
            <span>Reading + Response</span>
            {activity.reading_mode === "support" && (
              <span
                data-testid="lesson-workspace-reading-support-pill"
                className="inline-block px-2 py-0.5 rounded-full bg-[#96ded1]/30 text-[#1c2d3d] text-[9px] font-bold uppercase tracking-[0.15em] border border-[#96ded1]/60 normal-case-disabled"
                title="Support mode — shorter reading (150-350 words) with 2-question multiple-choice comprehension check"
              >
                Support
              </span>
            )}
          </p>
          <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>
            {activity.reading_markdown}
          </ReactMarkdown>
        </section>
      )}
      {/* Bulk download — one PDF bundling every student sheet, one per page.
          Guarded (Jun 2026 §3): only shown when a working download exists —
          real flat `student_sheets` on a non-unit. Units surface per-lesson
          bundles in the unit overview; a flat no-`lesson_index` export 404s. */}
      {hasSheets && activity.output_type !== "unit" && (
      <section data-testid="lesson-workspace-students-download">
        <button
          type="button"
          onClick={async () => {
            const toastId = toast.loading("Bundling every student sheet…", {
              description: "One combined PDF, one sheet per page.",
            });
            try {
              const res = await api.get(
                `/activities/${activity.id}/export`,
                { params: { format: "student_sheets" }, responseType: "blob", timeout: 60000 },
              );
              const cd = res.headers["content-disposition"] || "";
              const m = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(cd);
              const fname = m
                ? decodeURIComponent(m[1])
                : `${(activity.activity_name || "lesson").replace(/[^a-z0-9_-]+/gi, "_")}_StudentSheets.pdf`;
              const url = window.URL.createObjectURL(res.data);
              const a = document.createElement("a");
              a.href = url;
              a.download = fname;
              document.body.appendChild(a);
              a.click();
              a.remove();
              setTimeout(() => window.URL.revokeObjectURL(url), 1500);
              toast.success("Student sheets downloaded", { id: toastId, description: fname });
            } catch (err) {
              const detail =
                err?.response?.data?.detail ||
                err?.message ||
                "Couldn't bundle the student sheets. Please try again.";
              toast.error("Download failed", { id: toastId, description: String(detail) });
            }
          }}
          data-testid="lesson-workspace-download-student-sheets"
          className="w-full text-left rounded-xl border border-[#4a6984]/15 bg-white p-5 hover:border-[#96ded1] hover:bg-[#96ded1]/10 transition-colors flex flex-wrap items-center gap-4 cursor-pointer"
        >
          <FileText className="h-5 w-5 text-[#1c2d3d] shrink-0" />
          <div className="flex-1 min-w-[200px]">
            <p className="font-extrabold text-sm text-[#1c2d3d]">
              Download every student sheet
            </p>
            <p className="text-xs text-[#4a6984]/75 mt-0.5">
              One PDF you can print, with a sheet per page.
            </p>
          </div>
        </button>
      </section>
      )}
    </div>
  );
};

// Phase 127 — In-app preview tab. Surfaces the deck (HTML thumbnails
// from slide_deck_spec) and the worksheet (PDF rendered via react-pdf)
// from the SAME source as the export pipeline, plus two next-step
// CTAs per artifact (Export as-is / Open in Canva).
const PreviewTab = ({ activity, effectiveMarkdown }) => {
  // Phase 127 — `student_sheets` (handout + exit ticket — same artifact
  // bundled into the Complete Lesson Package) is the canonical source.
  // The legacy Transferable Skills Worksheet is consistently great and
  // doesn't need a preview, so it's intentionally NOT shown here.
  const hasStudentSheets = (activity.student_sheets?.length || 0) > 0;
  const hasDeck =
    activity.output_type !== "unit" &&
    activity.output_type !== "math_unit_summary";
  if (!hasStudentSheets && !hasDeck) {
    return (
      <p
        className="text-[#4a6984]/70 italic"
        data-testid="lesson-workspace-no-preview"
      >
        This activity doesn&apos;t produce a previewable deck or student sheets.
      </p>
    );
  }
  return (
    <div className="space-y-6" data-testid="lesson-workspace-preview">
      <p className="text-sm text-[#4a6984]/85 leading-snug max-w-2xl">
        See exactly what students will receive before you export. The preview
        is built from the same source as your downloaded file — what you see
        is what they get.
      </p>
      {hasDeck && (
        <DeckPreview
          activityId={activity.id}
          activityName={activity.activity_name}
        />
      )}
      {hasDeck && hasStudentSheets && (
        <DeckMirroringAudit activity={activity} />
      )}
      {hasStudentSheets && (
        <WorksheetPreview
          activityId={activity.id}
          activityName={activity.activity_name}
        />
      )}
    </div>
  );
};

const ReportingTab = ({ reportingMarkdown }) => {
  const parsed = useMemo(
    () => parseReportingSections(reportingMarkdown || ""),
    [reportingMarkdown],
  );
  // Track explicit user toggles, scoped to the current parsed shape so a new
  // lesson always opens its own default card. No setState-in-effect needed:
  // when the parsed shape changes, the scope key differs and overrides are
  // implicitly cleared at render time.
  const overridesKey = useMemo(
    () => parsed.sections.map((s) => s.id).join("|"),
    [parsed],
  );
  const [override, setOverride] = useState({ key: "", value: {} });
  const effective = override.key === overridesKey ? override.value : {};
  const defaultFirstId =
    parsed.style === "strand" && parsed.sections.length
      ? parsed.sections[0].id
      : null;
  const isOpenFor = (id) => {
    if (id in effective) return effective[id];
    if (parsed.style === "level") return id === "level3";
    return id === defaultFirstId;
  };
  const toggle = (id) =>
    setOverride({
      key: overridesKey,
      value: { ...effective, [id]: !isOpenFor(id) },
    });

  if (!reportingMarkdown) {
    return (
      <p className="text-[#4a6984]/70 italic" data-testid="lesson-workspace-no-reporting">
        This lesson doesn&apos;t include report card language.
      </p>
    );
  }

  const headerNote =
    parsed.style === "level"
      ? "language to your voice. Level 3 is the provincial standard — start there."
      : "language to your voice. Each card covers all four Growing Success levels (E / G / S / N).";

  return (
    <div data-testid="lesson-workspace-reporting" className="space-y-4 max-w-3xl">
      <p className="text-xs text-[#4a6984]/85 font-medium border-l-4 border-[#96ded1] pl-3 py-1">
        Replace <strong className="text-[#1c2d3d]">[Name]</strong> and adapt the
        {" "}{headerNote}
      </p>
      {parsed.intro && (
        <div className="ae-prose" data-testid="lesson-workspace-reporting-intro">
          <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>
            {parsed.intro}
          </ReactMarkdown>
        </div>
      )}
      {parsed.sections.map((sec) => {
        const isOpen = isOpenFor(sec.id);
        return (
          <div
            key={sec.id}
            data-testid={`lesson-workspace-reporting-${sec.id}`}
            className="rounded-lg border border-[#4a6984]/15 bg-white overflow-hidden"
          >
            <button
              type="button"
              onClick={() => toggle(sec.id)}
              data-testid={`lesson-workspace-reporting-${sec.id}-toggle`}
              className="w-full flex items-center justify-between gap-3 px-4 py-3 text-left hover:bg-[#f4f5f7]"
            >
              <span className="font-extrabold text-sm text-[#1c2d3d] uppercase tracking-[0.1em]">
                {sec.label}
              </span>
              <ChevronDown
                className={`h-4 w-4 text-[#4a6984]/70 transition-transform ${
                  isOpen ? "rotate-180" : ""
                }`}
              />
            </button>
            {isOpen && (
              <div className="px-4 pb-4 ae-prose">
                <ReactMarkdown
                  remarkPlugins={[remarkGfm]}
                  components={markdownComponents}
                >
                  {sec.body}
                </ReactMarkdown>
              </div>
            )}
          </div>
        );
      })}
      {/* Fallback: if no structured sections detected at all, render verbatim
          so we never hide content. */}
      {parsed.style === "flat" && (
        <div className="ae-prose" data-testid="lesson-workspace-reporting-raw">
          <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>
            {reportingMarkdown}
          </ReactMarkdown>
        </div>
      )}
      <p className="text-[11px] text-[#4a6984]/65 pt-4">
        <Download className="h-3 w-3 inline -mt-0.5 mr-1" />
        Report-card language appears as the final appendix in the combined
        Lesson Package PDF.
      </p>
    </div>
  );
};

/**
 * parseReportingSections lives in /lib/reportingSections.js so it can be
 * unit-tested without pulling in the full LessonWorkspace page tree.
 */

export default LessonWorkspace;
